import React, { useState, useEffect, useCallback } from 'react';
import { MENU_ITEMS } from '../constants/menuItems';
import TabBar from '../components/TabBar';

function getToken() { return window.__cafeApiToken || localStorage.getItem('cafepos_token'); }

// ---------------------------------------------------------------------------
// Komponent statusu agenta
// ---------------------------------------------------------------------------
function FiscalAgentStatus({ agentUrl }) {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  async function check() {
    setLoading(true);
    try {
      const res  = await fetch('/api/fiscal/status', { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      setStatus(data);
    } catch { setStatus({ ok: false, error: 'Brak połączenia z serwerem' }); }
    finally { setLoading(false); }
  }

  const dot = s => s === 'online'
    ? <span style={{ color: 'var(--green)' }}>✓ online</span>
    : <span style={{ color: 'var(--red)' }}>✗ offline</span>;

  return (
    <div style={{ marginTop: 12 }}>
      <button type="button" onClick={check} disabled={loading || !agentUrl}
        style={{ padding: '7px 16px', borderRadius: 6, border: '0.5px solid var(--border)', background: 'var(--bg)', fontSize: 13, cursor: 'pointer' }}>
        {loading ? '⏳ Sprawdzam...' : '🔌 Sprawdź połączenie'}
      </button>
      {status && (
        <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, fontSize: 13,
          background: status.ok ? 'var(--bg)' : '#FCEBEB', border: '0.5px solid var(--border)',
          color: status.ok ? 'var(--text)' : 'var(--red)' }}>
          {status.ok ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div>🖨️ Agent: <span style={{ color: 'var(--green)' }}>✓ online</span></div>
              <div>🧾 Drukarka bonowa: {dot(status.receipt_printer?.status)}</div>
              <div>🏦 Drukarka fiskalna: {dot(status.fiscal_printer?.status)}</div>
            </div>
          ) : `✗ ${status.error}`}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sekcja z tytułem
// ---------------------------------------------------------------------------
function Section({ title, children }) {
  return (
    <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: 20, marginBottom: 16 }}>
      {title && <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 16, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{title}</h3>}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Główny komponent
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Komponent kont bankowych
// ---------------------------------------------------------------------------
function BankAccounts({ value, onChange }) {
  let accounts = [];
  try { accounts = JSON.parse(value || '[]'); } catch {}
  if (!Array.isArray(accounts)) accounts = [];

  function update(list) { onChange(JSON.stringify(list)); }

  function addAccount() {
    update([...accounts, { bank: '', iban: '' }]);
  }

  function removeAccount(i) {
    update(accounts.filter((_, idx) => idx !== i));
  }

  function setField(i, k, v) {
    update(accounts.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  }

  // Formatuj IBAN — dodaj spacje co 4 znaki
  function formatIban(raw) {
    return raw.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
  }

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Konta bankowe</div>
      {accounts.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Brak kont bankowych</div>
      )}
      {accounts.map((acc, i) => (
        <div key={i} style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Konto {i + 1}</span>
            <button onClick={() => removeAccount(i)}
              style={{ background: 'none', border: 'none', color: 'var(--red)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          <div className="field" style={{ margin: '0 0 8px' }}>
            <label>Nazwa banku</label>
            <input value={acc.bank} onChange={e => setField(i, 'bank', e.target.value)} placeholder="np. PKO BP, mBank, Santander" />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Numer konta (IBAN)</label>
            <input value={acc.iban} onChange={e => setField(i, 'iban', formatIban(e.target.value))}
              placeholder="PL 00 0000 0000 0000 0000 0000 0000" maxLength={40}
              style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }} />
          </div>
        </div>
      ))}
      <button type="button" onClick={addAccount}
        style={{ padding: '8px 16px', borderRadius: 8, border: '0.5px dashed var(--border)', background: 'none', fontSize: 13, cursor: 'pointer', color: 'var(--text-muted)', width: '100%' }}>
        + Dodaj konto bankowe
      </button>
    </div>
  );
}

export default function Settings() {
  const TABS = [
    { id: 'general',     label: '🏢 Ogólne'      },
    { id: 'hardware',    label: '🖨️ Sprzęt'      },
    { id: 'receipt',     label: '🧾 Paragony'     },
    { id: 'fiscal',      label: '📠 Paragony fiskalne' },
    { id: 'invoices',    label: '📄 Faktury'      },
    { id: 'email',       label: '📧 E-mail'       },
    { id: 'views',       label: '👁️ Widoki'       },
    { id: 'kds',         label: '🖥️ KDS'          },
    { id: 'permissions', label: '🔐 Uprawnienia'  },
  ];

  const [tab,  setTab]  = useState('general');
  const [form, setForm] = useState({
    // Ogólne
    pos_name:             '',
    pos_address:          '',
    pos_zip:              '',
    pos_city:             '',
    pos_phone:            '',
    pos_email:            '',
    pos_website:          '',
    ksef_company_name:    '',
    ksef_nip:             '',
    ksef_company_address: '',
    ksef_company_zip:     '',
    ksef_company_city:    '',
    ksef_regon:           '',
    ksef_email:           '',
    bank_accounts:        '[]',
    // Sprzęt
    terminal_timeout:     '120',
    cash_report_close_roles: 'admin,manager',
    printer_ip:           '',
    printer_port:         '9100',
    drawer_on_cash:       '1',
    drawer_on_card:       '0',
    drawer_on_blik:       '0',
    drawer_on_receipt:    '0',
    receipt_printer_ip:   '',
    receipt_printer_port: '9100',
    receipt_on_send:      '0',
    receipt_on_pay:       '1',
    receipt_on_account:   '0',
    account_slip_use_fiscal: '0',
    pre_bill_use_fiscal: '0',
    kds_font_name:     '24',
    kds_font_variant:  '20',
    kds_color_ready:   '#27AE60',
    kds_color_delivered: '#444444',
    kds_age_warn:      '300',
    kds_age_danger:    '600',
    kds_sound_enabled: '1',
    kds_grid_columns:  'auto',
    kds_show_category: '0',
    fiscal_agent_url:     '',
    fiscal_agent_secret:  '',
    drawer_source:        'receipt',
    receipt_printer_target: 'receipt',
    fiscal_on_pay:        '1',
    // Paragony
    receipt_header:       '',
    receipt_address:      '',
    receipt_footer:       'Dziękujemy za wizytę!',
    receipt_template:     'classic',
    receipt_show_nip:     '1',
    receipt_show_phone:   '1',
    receipt_show_email:   '0',
    receipt_show_website: '0',
    receipt_show_table:   '1',
    receipt_show_order_no:'1',
    receipt_show_date:    '1',
    receipt_show_payment: '1',
    receipt_show_vat:     '0',
    receipt_show_pos_name:     '1',
    receipt_show_company_name: '0',
    receipt_show_address:      '1',
    receipt_custom_line1: '',
    receipt_custom_line2: '',
    // Faktury
    ksef_token:           '',
    ksef_cert_pem:        '',
    ksef_cert_key_pem:    '',
    ksef_context_nip:     '',
    ksef_confirmation_auto_print: '1',
    invoice_number_format_fv: '{PREFIX}/{YEAR}/{MONTH}/{SEQ}',
    invoice_number_format_pn: '{PREFIX}/{YEAR}/{MONTH}/{SEQ}',
    invoice_number_prefix_fv: 'FV',
    invoice_number_prefix_pn: 'PN',
    invoice_number_reset_fv: 'yearly',
    invoice_number_reset_pn: 'yearly',
    gus_api_key:          '',
    // Paragony fiskalne (stopka drukarki fiskalnej Posnet)
    fiscal_footer_facebook_enabled:  '0',
    fiscal_footer_facebook:          '',
    fiscal_footer_instagram_enabled: '0',
    fiscal_footer_instagram:         '',
    fiscal_footer_text_enabled:      '0',
    fiscal_footer_text:              '',
    fiscal_footer_qr_enabled:        '0',
    fiscal_footer_qr_url:            '',
    // E-mail (SMTP)
    smtp_host:             '',
    smtp_port:              '587',
    smtp_secure:            '0',
    smtp_user:              '',
    smtp_pass:              '',
    smtp_from:              '',
    cash_report_email_to:  '',
    category_tabs_per_row: '6',
  });

  const [saved,            setSaved]            = useState(false);
  const [testing,          setTesting]          = useState(false);
  const [testMsg,          setTestMsg]          = useState('');
  const [logoUrl,          setLogoUrl]          = useState(null);
  const [logoUploading,    setLogoUploading]    = useState(false);
  const [pinLogoUrl,       setPinLogoUrl]       = useState(null);
  const [pinLogoUploading, setPinLogoUploading] = useState(false);
  const [roles,            setRoles]            = useState([]);

  const loadRoles = useCallback(() => {
    fetch('/api/roles', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(setRoles);
  }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function toggleMenuVisible(role, key) {
    const current = role.visible_menu_keys || [];
    const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    // Optymistyczna aktualizacja UI
    setRoles(rs => rs.map(r => r.id === role.id ? { ...r, visible_menu_keys: next } : r));
    try {
      const res = await fetch(`/api/roles/${role.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ visible_menu_keys: next }),
      });
      if (!res.ok) throw new Error();
    } catch {
      loadRoles(); // cofnij optymistyczną zmianę jeśli zapis się nie udał
    }
  }

  // Wczytaj ustawienia
  useEffect(() => {
    fetch('/api/settings', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(s => setForm(f => ({ ...f, ...s })));
    fetch('/api/receipt/logo', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => { if (d.exists) fetchLogoDataUrl(); });
    fetch('/api/receipt/pin-logo', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => { if (d.exists) fetchPinLogoDataUrl(); });
    loadRoles();
  }, [loadRoles]);

  async function fetchLogoDataUrl() {
    try {
      const res = await fetch('/api/receipt/logo/preview', { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) return;
      const blob   = await res.blob();
      const reader = new FileReader();
      reader.onload = () => setLogoUrl(reader.result);
      reader.readAsDataURL(blob);
    } catch {}
  }

  async function fetchPinLogoDataUrl() {
    try {
      const res = await fetch('/api/receipt/pin-logo/preview', { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) return;
      const blob   = await res.blob();
      const reader = new FileReader();
      reader.onload = () => setPinLogoUrl(reader.result);
      reader.readAsDataURL(blob);
    } catch {}
  }

  async function save() {
    await fetch('/api/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify(form),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  async function uploadLogo(file) {
    if (!file || !['image/png','image/jpeg','image/bmp'].includes(file.type)) { alert('Wybierz plik PNG, JPG lub BMP'); return; }
    if (file.size > 2.5 * 1024 * 1024) { alert('Logo może mieć max 2.5MB'); return; }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await fetch('/api/receipt/logo', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!res.ok) throw new Error((await res.json()).error);
      await fetchLogoDataUrl();
    } catch (err) { alert('Błąd uploadu: ' + err.message); }
    finally { setLogoUploading(false); }
  }

  async function deleteLogo() {
    await fetch('/api/receipt/logo', { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
    setLogoUrl(null);
  }

  async function uploadPinLogo(file) {
    if (file.size > 2.5 * 1024 * 1024) { alert('Logo może mieć max 2.5MB'); return; }
    setPinLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await fetch('/api/receipt/pin-logo', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (res.ok) await fetchPinLogoDataUrl();
    } finally { setPinLogoUploading(false); }
  }

  async function deletePinLogo() {
    await fetch('/api/receipt/pin-logo', { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
    setPinLogoUrl(null);
  }

  async function testReceipt() {
    setTesting(true); setTestMsg('');
    try {
      const res  = await fetch('/api/receipt/print', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ order_id: 0, test: true }) });
      const data = await res.json();
      setTestMsg(res.ok ? '✓ Bon wydrukowany' : `✗ ${data.error}`);
    } catch { setTestMsg('✗ Błąd połączenia'); }
    finally { setTesting(false); }
  }

  async function testDrawer() {
    setTesting(true); setTestMsg('');
    try {
      const res  = await fetch('/api/drawer/open', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      setTestMsg(res.ok ? '✓ Szuflada otwarta' : `✗ ${data.error}`);
    } catch { setTestMsg('✗ Błąd połączenia'); }
    finally { setTesting(false); }
  }

  function buildPreview(s) {
    const W = 32;
    const tpl  = s.receipt_template || 'classic';
    const show = {
      nip:          s.receipt_show_nip          !== '0',
      phone:        s.receipt_show_phone        !== '0',
      email:        s.receipt_show_email        !== '0',
      website:      s.receipt_show_website      !== '0',
      table:        s.receipt_show_table        !== '0',
      order_no:     s.receipt_show_order_no     !== '0',
      date:         s.receipt_show_date         !== '0',
      payment:      s.receipt_show_payment      !== '0',
      vat:          s.receipt_show_vat          !== '0',
      pos_name:     s.receipt_show_pos_name     !== '0',
      company_name: s.receipt_show_company_name !== '0',
      address:      s.receipt_show_address      !== '0',
    };
    const posName = s.pos_name || 'CafePOS';
    const posAddr = s.pos_address || '';
    const posCity = [s.pos_zip, s.pos_city].filter(Boolean).join(' ');
    const footer  = s.receipt_footer || 'Dziękujemy za wizytę!';
    const nip     = s.ksef_nip || '';
    const ln  = (l, r='') => { const sp = W-l.length-r.length; return l+' '.repeat(Math.max(1,sp))+r; };
    const div = (c='-') => c.repeat(W);
    const ctr = str => { const p = Math.max(0,Math.floor((W-str.length)/2)); return ' '.repeat(p)+str; };
    const lines = []; const add = (...s) => s.forEach(x => lines.push(x));
    const items = [{ name:'Cappuccino',qty:2,price:14,vat_rate:8 },{ name:'Croissant maślany',qty:1,price:8.5,vat_rate:5 }];
    const total = items.reduce((s,i) => s+i.price*i.qty, 0);
    function vg(items) { const g={}; items.forEach(i => { const r=Number(i.vat_rate??23); const gross=i.price*i.qty; if(!g[r])g[r]={gross:0,net:0,vat:0}; const net=gross/(1+r/100); g[r].gross+=gross;g[r].net+=net;g[r].vat+=gross-net; }); return g; }

    if (tpl==='classic') {
      if(show.pos_name)add(ctr(posName));
      if(show.company_name&&s.ksef_company_name)add(ctr(s.ksef_company_name));
      if(show.address){if(posAddr)add(ctr(posAddr)); if(posCity)add(ctr(posCity));}
      if(show.phone&&s.pos_phone)add(ctr('Tel: '+s.pos_phone)); if(show.email&&s.pos_email)add(ctr(s.pos_email));
      if(show.website&&s.pos_website)add(ctr(s.pos_website)); if(show.nip&&nip)add(ctr('NIP: '+nip));
      add('',div()); if(show.table)add(ln('Stolik:','Taras')); if(show.date)add(ln('Data:','25.06.2026 12:30')); if(show.order_no)add(ln('Nr zam.:','#42'));
      add(div()); items.forEach(i=>add(ln(`${i.qty}x ${i.name}`,`${(i.price*i.qty).toFixed(2)} zl`)));
      add(div(),ln('SUMA:',`${total.toFixed(2)} zl`)); if(show.payment)add(ln('Płatność:','Karta'));
      if(show.vat){const g=vg(items);Object.entries(g).forEach(([r,v])=>{const l=Number(r)===0?'ZW':`VAT ${r}%`;add(div(),ln(`Netto ${l}:`,`${v.net.toFixed(2)} zl`),ln(`${l}:`,`${v.vat.toFixed(2)} zl`));});}
    } else if(tpl==='modern') {
      if(show.pos_name)add(ctr('*** '+posName+' ***'));
      if(show.company_name&&s.ksef_company_name)add(ctr(s.ksef_company_name));
      if(show.address){if(posAddr)add(ctr(posAddr)); if(posCity)add(ctr(posCity));}
      if(show.nip&&nip)add(ctr('NIP: '+nip)); add('','='.repeat(W));
      if(show.table)add(ln('> Stolik:','Taras')); if(show.date)add(ln('> Data:','25.06.2026'),ln('> Godz.:','12:30')); if(show.order_no)add(ln('> Nr:','#42'));
      add('='.repeat(W)); items.forEach(i=>{add(`${i.qty}x ${i.name}`,ln('',`${(i.price*i.qty).toFixed(2)} zl`));});
      add('='.repeat(W),ctr(`RAZEM: ${total.toFixed(2)} zl`)); if(show.payment)add(ctr('Karta'));
      if(show.vat){const g=vg(items);Object.entries(g).forEach(([r,v])=>{const l=Number(r)===0?'ZW':`VAT ${r}%`;add(ctr(`${l}: ${v.vat.toFixed(2)} zl`));});}
    } else if(tpl==='minimal') {
      if(show.pos_name)add(ctr(posName));
      if(show.company_name&&s.ksef_company_name)add(ctr(s.ksef_company_name));
      if(show.nip&&nip)add(ctr('NIP: '+nip)); add('');
      if(show.date)add(ln('25.06.2026','12:30')); if(show.table||show.order_no)add(ln(show.table?'Taras':'',show.order_no?'#42':''));
      add(''); items.forEach(i=>add(ln(`${i.qty}x ${i.name}`,`${(i.price*i.qty).toFixed(2)}`)));
      add('',ln('SUMA',`${total.toFixed(2)} zł`)); if(show.payment)add('Karta');
    } else {
      if(show.pos_name)add(ctr(posName));
      if(show.company_name&&s.ksef_company_name)add(ctr(s.ksef_company_name));
      if(show.address){if(posAddr)add(ctr(posAddr)); if(posCity)add(ctr(posCity));}
      if(show.nip&&nip)add(ctr('NIP: '+nip)); add('','*'.repeat(W));
      if(show.table)add(ln('Stolik:','Taras')); if(show.date)add(ln('Data:','25.06.2026'),ln('Godzina:','12:30')); if(show.order_no)add(ln('Nr zamowienia:','#42'));
      add('*'.repeat(W),ln('NAZWA','KWOTA'),div()); items.forEach(i=>{add(ln(i.name.substring(0,22),`${(i.price*i.qty).toFixed(2)} zl`));});
      add(div(),ln('DO ZAPLATY:',`${total.toFixed(2)} zl`)); if(show.payment)add(ln('Forma płatności:','Karta'));
      if(show.vat){const g=vg(items);add(div());Object.entries(g).forEach(([r,v])=>{const l=Number(r)===0?'ZW':`${r}%`;add(ln(`Netto ${l}:`,`${v.net.toFixed(2)} zl`),ln(`VAT ${l}:`,`${v.vat.toFixed(2)} zl`),ln(`Brutto ${l}:`,`${v.gross.toFixed(2)} zl`));});add(div(),ln('RAZEM BRUTTO:',`${total.toFixed(2)} zl`));}
    }
    if(s.receipt_custom_line1||s.receipt_custom_line2){add('');if(s.receipt_custom_line1)add(ctr(s.receipt_custom_line1));if(s.receipt_custom_line2)add(ctr(s.receipt_custom_line2));}
    add('',ctr(footer),'','--------------------------------');
    return lines.join('\n');
  }

  const cardStyle = { background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: 20 };
  const sectionTitle = { fontSize: 13, fontWeight: 600, marginBottom: 16, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' };
  const divider = { borderTop: '0.5px solid var(--border)', margin: '16px 0' };

  return (
    <div style={{ padding: 24, maxWidth: 1400, width: '100%', boxSizing: 'border-box' }}>
      <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 20 }}>Ustawienia</h2>

      {/* Zakładki */}
      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16, alignItems: 'start' }}>

      {/* ================================================================ */}
      {/* ZAKŁADKA: OGÓLNE                                                  */}
      {/* ================================================================ */}
      {tab === 'general' && (
        <>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Punkt sprzedaży</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>Drukowane na paragonach i widoczne dla klientów.</div>
            <div className="field"><label>Nazwa punktu</label><input value={form.pos_name} onChange={e => set('pos_name', e.target.value)} placeholder="np. IT Cafe Bar — Centrum" /></div>
            <div className="field"><label>Adres</label><input value={form.pos_address} onChange={e => set('pos_address', e.target.value)} placeholder="ul. Przykładowa 1" /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="field" style={{ flex: 1, margin: 0 }}><label>Kod pocztowy</label><input value={form.pos_zip} onChange={e => set('pos_zip', e.target.value)} placeholder="00-000" maxLength={6} /></div>
              <div className="field" style={{ flex: 2, margin: 0 }}><label>Miasto</label><input value={form.pos_city} onChange={e => set('pos_city', e.target.value)} placeholder="Warszawa" /></div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <div className="field" style={{ flex: 1, margin: 0 }}><label>Telefon</label><input value={form.pos_phone} onChange={e => set('pos_phone', e.target.value)} placeholder="+48 000 000 000" /></div>
              <div className="field" style={{ flex: 1, margin: 0 }}><label>E-mail</label><input type="email" value={form.pos_email} onChange={e => set('pos_email', e.target.value)} placeholder="kontakt@kawiarnia.pl" /></div>
            </div>
            <div className="field" style={{ marginTop: 10 }}><label>Strona www</label><input value={form.pos_website} onChange={e => set('pos_website', e.target.value)} placeholder="www.kawiarnia.pl" /></div>
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>Wygląd zamówień</h3>
            <div className="field">
              <label>Maks. liczba zakładek kategorii w jednym wierszu</label>
              <input type="number" min="1" max="12" value={form.category_tabs_per_row || '6'}
                onChange={e => set('category_tabs_per_row', e.target.value)} placeholder="np. 6" />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Gdy kategorii jest więcej, kolejne zawijają się do następnego wiersza. Każda zakładka zajmuje równą część szerokości.
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>Dane firmy</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>Używane jako wystawca na fakturach.</div>
            <div className="field"><label>Nazwa firmy</label><input value={form.ksef_company_name} onChange={e => set('ksef_company_name', e.target.value)} placeholder="np. Kawiarnia Brzozowa Sp. z o.o." /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="field" style={{ flex: 1, margin: 0 }}><label>NIP</label><input value={form.ksef_nip} onChange={e => set('ksef_nip', e.target.value)} placeholder="0000000000" maxLength={10} /></div>
              <div className="field" style={{ flex: 1, margin: 0 }}><label>REGON</label><input value={form.ksef_regon} onChange={e => set('ksef_regon', e.target.value)} placeholder="000000000" maxLength={14} /></div>
            </div>
            <div className="field"><label>E-mail firmy</label><input type="email" value={form.ksef_email} onChange={e => set('ksef_email', e.target.value)} placeholder="biuro@firma.pl" /></div>
            <div className="field"><label>Adres</label><input value={form.ksef_company_address} onChange={e => set('ksef_company_address', e.target.value)} placeholder="ul. Przykładowa 1" /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="field" style={{ flex: 1, margin: 0 }}><label>Kod pocztowy</label><input value={form.ksef_company_zip} onChange={e => set('ksef_company_zip', e.target.value)} placeholder="00-000" maxLength={6} /></div>
              <div className="field" style={{ flex: 2, margin: 0 }}><label>Miasto</label><input value={form.ksef_company_city} onChange={e => set('ksef_company_city', e.target.value)} placeholder="Warszawa" /></div>
            </div>

            <div style={divider} />
            <BankAccounts value={form.bank_accounts} onChange={v => set('bank_accounts', v)} />
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>Terminal</h3>
            <div className="field">
              <label>Timeout bezczynności (sekundy)</label>
              <input type="number" min="30" max="600" value={form.terminal_timeout} onChange={e => set('terminal_timeout', e.target.value)} />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Po tym czasie terminal wraca do ekranu PIN.</div>
            </div>
          </div>



          <div style={cardStyle}>
            <h3 style={sectionTitle}>Logo ekranu PIN</h3>
            {pinLogoUrl ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <img src={pinLogoUrl} alt="Logo PIN" style={{ maxHeight: 80, maxWidth: 200, border: '0.5px solid var(--border)', borderRadius: 6, padding: 8, background: '#fff' }} />
                  <button onClick={deletePinLogo} style={{ padding: '6px 14px', borderRadius: 6, border: '0.5px solid var(--red)', background: 'none', color: 'var(--red)', fontSize: 13, cursor: 'pointer' }}>🗑 Usuń</button>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 16, padding: 20, border: '2px dashed var(--border)', borderRadius: 8, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                Brak logo — zamiast niego wyświetla się ikona ☕
              </div>
            )}
            <label style={{ display: 'inline-block' }}>
              <input type="file" accept="image/png,image/jpeg,image/bmp,image/svg+xml" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadPinLogo(e.target.files[0])} />
              <div style={{ padding: '9px 18px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--bg)', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {pinLogoUploading ? '⏳ Wgrywanie...' : '📁 Wybierz plik (PNG, JPG — max 2.5MB)'}
              </div>
            </label>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* ZAKŁADKA: SPRZĘT                                                  */}
      {/* ================================================================ */}
      {tab === 'hardware' && (
        <>
          {/* Drukarka bonowa */}
          <div style={cardStyle}>
            <h3 style={sectionTitle}>🧾 Drukarka bonowa (ESC/POS)</h3>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="field" style={{ flex: 2, margin: 0 }}><label>Adres IP</label><input value={form.receipt_printer_ip} onChange={e => set('receipt_printer_ip', e.target.value)} placeholder="192.168.1.211" /></div>
              <div className="field" style={{ flex: 1, margin: 0 }}><label>Port</label><input type="number" value={form.receipt_printer_port} onChange={e => set('receipt_printer_port', e.target.value)} placeholder="9100" /></div>
            </div>
            <div style={{ marginTop: 16, marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Drukuj bon automatycznie:</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.receipt_on_send === '1'} onChange={e => set('receipt_on_send', e.target.checked ? '1' : '0')} />
                Po wysłaniu zamówienia do kuchni
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.receipt_on_pay === '1'} onChange={e => set('receipt_on_pay', e.target.checked ? '1' : '0')} />
                Po opłaceniu zamówienia
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.receipt_on_account === '1'} onChange={e => set('receipt_on_account', e.target.checked ? '1' : '0')} />
                Po wysłaniu "Na konto" kontrahenta
              </label>
              {form.receipt_on_account === '1' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0 6px 24px', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>
                  <input type="checkbox" checked={form.account_slip_use_fiscal === '1'} onChange={e => set('account_slip_use_fiscal', e.target.checked ? '1' : '0')} />
                  Drukuj na drukarce fiskalnej zamiast bonowej (niefiskalny wydruk "ZAMÓWIENIE")
                </label>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13, marginTop: 8 }}>
                <input type="checkbox" checked={form.pre_bill_use_fiscal === '1'} onChange={e => set('pre_bill_use_fiscal', e.target.checked ? '1' : '0')} />
                Rachunek wstępny na drukarce fiskalnej (zamiast bonowej)
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
              <button onClick={testReceipt} disabled={testing}
                style={{ padding: '8px 16px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--bg)', fontSize: 13, cursor: 'pointer' }}>
                {testing ? 'Drukowanie...' : '🖨️ Testuj drukarkę'}
              </button>
              {testMsg && <span style={{ fontSize: 13, color: testMsg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{testMsg}</span>}
            </div>
          </div>

          {/* Drukarka fiskalna */}
          <div style={cardStyle}>
            <h3 style={sectionTitle}>🏦 Drukarka fiskalna (Posnet)</h3>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="field" style={{ flex: 2, margin: 0 }}><label>Adres IP</label><input value={form.printer_ip} onChange={e => set('printer_ip', e.target.value)} placeholder="192.168.1.100" /></div>
              <div className="field" style={{ flex: 1, margin: 0 }}><label>Port</label><input type="number" value={form.printer_port} onChange={e => set('printer_port', e.target.value)} placeholder="9100" /></div>
            </div>
            <div style={{ marginTop: 16, marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Paragon fiskalny:</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.fiscal_on_pay === '1'} onChange={e => set('fiscal_on_pay', e.target.checked ? '1' : '0')} />
                Drukuj paragon fiskalny po opłaceniu
              </label>
            </div>
            <div style={{ marginTop: 16, marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Szuflada otwiera się przy płatności:</div>
              {[['cash','Gotówka'],['card','Karta'],['blik','BLIK'],['receipt','Paragon']].map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={form[`drawer_on_${key}`] === '1'} onChange={e => set(`drawer_on_${key}`, e.target.checked ? '1' : '0')} />
                  {label}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
              <button onClick={testDrawer} disabled={testing}
                style={{ padding: '8px 16px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--bg)', fontSize: 13, cursor: 'pointer' }}>
                {testing ? 'Testowanie...' : '🔔 Testuj szufladę'}
              </button>
              {testMsg && <span style={{ fontSize: 13, color: testMsg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{testMsg}</span>}
            </div>
          </div>

          {/* Agent */}
          <div style={cardStyle}>
            <h3 style={sectionTitle}>🔌 Agent lokalny</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              Skrypt Node.js na komputerze w kawiarni łączący serwer z drukarkami przez LAN.
            </div>
            <div className="field"><label>Adres agenta</label>
              <input value={form.fiscal_agent_url} onChange={e => set('fiscal_agent_url', e.target.value)} placeholder="79.162.215.14:3002" />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Publiczny IP:port lub domena. Protokół http:// zostanie dodany automatycznie.</div>
            </div>
            <div className="field"><label>Klucz zabezpieczający</label>
              <input type="password" value={form.fiscal_agent_secret} onChange={e => set('fiscal_agent_secret', e.target.value)} placeholder="Tajny klucz z agent.js" />
            </div>
            <FiscalAgentStatus agentUrl={form.fiscal_agent_url} />

            <div style={divider} />
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Routing sprzętu</div>
            <div className="field"><label>Drukuj bony zamówień na</label>
              <select value={form.receipt_printer_target} onChange={e => set('receipt_printer_target', e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 14, background: 'var(--surface)' }}>
                <option value="receipt">🧾 Drukarce bonowej (ESC/POS)</option>
                <option value="fiscal">🏦 Drukarce fiskalnej (Posnet)</option>
              </select>
            </div>
            <div className="field"><label>Szuflada podłączona do</label>
              <select value={form.drawer_source} onChange={e => set('drawer_source', e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 6, fontSize: 14, background: 'var(--surface)' }}>
                <option value="receipt">🧾 Drukarki bonowej</option>
                <option value="fiscal">🏦 Drukarki fiskalnej (Posnet)</option>
              </select>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* ZAKŁADKA: PARAGONY                                                */}
      {/* ================================================================ */}
      {tab === 'receipt' && (
        <>
          {/* Logo */}
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Logo</h3>
            {logoUrl ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <img src={logoUrl} alt="Logo" style={{ maxHeight: 80, maxWidth: 200, border: '0.5px solid var(--border)', borderRadius: 6, padding: 8, background: '#fff' }} />
                  <button onClick={deleteLogo} style={{ padding: '6px 14px', borderRadius: 6, border: '0.5px solid var(--red)', background: 'none', color: 'var(--red)', fontSize: 13, cursor: 'pointer' }}>🗑 Usuń</button>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 16, padding: 20, border: '2px dashed var(--border)', borderRadius: 8, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                Brak logo
              </div>
            )}
            <label style={{ display: 'inline-block' }}>
              <input type="file" accept="image/png,image/jpeg,image/bmp" style={{ display: 'none' }} onChange={e => e.target.files[0] && uploadLogo(e.target.files[0])} />
              <div style={{ padding: '9px 18px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--bg)', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {logoUploading ? '⏳ Wgrywanie...' : '📁 Wybierz plik (PNG, JPG, BMP — max 2.5MB)'}
              </div>
            </label>
          </div>

          {/* Stopka */}
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Nagłówek i stopka</h3>
            <div className="field"><label>Stopka bonu</label><input value={form.receipt_footer} onChange={e => set('receipt_footer', e.target.value)} placeholder="Dziękujemy za wizytę!" /></div>
            <div className="field"><label>Dodatkowa linia 1</label><input value={form.receipt_custom_line1} onChange={e => set('receipt_custom_line1', e.target.value)} placeholder="np. @kawiarnia" maxLength={32} /></div>
            <div className="field"><label>Dodatkowa linia 2</label><input value={form.receipt_custom_line2} onChange={e => set('receipt_custom_line2', e.target.value)} placeholder="np. kawiarnia.pl/menu" maxLength={32} /></div>
          </div>

          {/* Szablon */}
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Szablon</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
              {[['classic','Klasyczny','Tradycyjny układ'],['modern','Nowoczesny','Większa czcionka'],['minimal','Minimalny','Tylko niezbędne'],['detailed','Szczegółowy','Ceny jednostkowe, VAT']].map(([v,l,d]) => (
                <button key={v} onClick={() => set('receipt_template', v)} type="button"
                  style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                    border: form.receipt_template === v ? '2px solid var(--blue)' : '0.5px solid var(--border)',
                    background: form.receipt_template === v ? 'var(--blue-light)' : 'var(--bg)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: form.receipt_template === v ? 'var(--blue-dark)' : 'var(--text)' }}>{l}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{d}</div>
                </button>
              ))}
            </div>

            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Pokaż na paragonie:</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, marginBottom: 20 }}>
              {[
                ['receipt_show_pos_name',    'Nazwa punktu sprzedaży'],
                ['receipt_show_company_name','Nazwa firmy'],
                ['receipt_show_address',     'Adres'],
                ['receipt_show_nip',         'NIP firmy'],
                ['receipt_show_phone',       'Telefon'],
                ['receipt_show_email',       'E-mail'],
                ['receipt_show_website',     'Strona www'],
                ['receipt_show_table',       'Numer stolika'],
                ['receipt_show_order_no',    'Numer zamówienia'],
                ['receipt_show_date',        'Data i godzina'],
                ['receipt_show_payment',     'Forma płatności'],
                ['receipt_show_vat',         'Podsumowanie VAT'],
              ].map(([k,l]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={form[k] !== '0'} onChange={e => set(k, e.target.checked ? '1' : '0')} />
                  {l}
                </label>
              ))}
            </div>

            {/* Podgląd */}
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Podgląd:</div>
            <div style={{ background: '#fff', border: '1px dashed var(--border)', borderRadius: 6, padding: '12px 16px', fontFamily: 'monospace', fontSize: 11, lineHeight: 1.6, color: '#111', overflowX: 'auto' }}>
              {logoUrl && (
                <div style={{ textAlign: 'center', marginBottom: 8, paddingBottom: 8, borderBottom: '1px dashed #ccc' }}>
                  <img src={logoUrl} alt="Logo" style={{ maxWidth: 160, maxHeight: 60, filter: 'grayscale(100%)' }} />
                </div>
              )}
              <div style={{ whiteSpace: 'pre' }}>{buildPreview(form)}</div>
            </div>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* ZAKŁADKA: PARAGONY FISKALNE (stopka drukarki Posnet)              */}
      {/* ================================================================ */}
      {tab === 'fiscal' && (
        <div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Stopka paragonu fiskalnego</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, marginBottom: 16 }}>
              Te elementy drukują się na fizycznym paragonie z drukarki fiskalnej (Posnet) — niezależnie od bonu ESC/POS.
            </p>

            {/* Instagram */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              <input type="checkbox" checked={form.fiscal_footer_instagram_enabled === '1'}
                onChange={e => set('fiscal_footer_instagram_enabled', e.target.checked ? '1' : '0')} />
              Instagram
            </label>
            <div className="field" style={{ marginBottom: 16 }}>
              <input value={form.fiscal_footer_instagram} onChange={e => set('fiscal_footer_instagram', e.target.value)}
                placeholder="np. instagram.com/itcb_itcafeandbar" disabled={form.fiscal_footer_instagram_enabled !== '1'} />
            </div>

            {/* Facebook */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              <input type="checkbox" checked={form.fiscal_footer_facebook_enabled === '1'}
                onChange={e => set('fiscal_footer_facebook_enabled', e.target.checked ? '1' : '0')} />
              Facebook
            </label>
            <div className="field" style={{ marginBottom: 16 }}>
              <input value={form.fiscal_footer_facebook} onChange={e => set('fiscal_footer_facebook', e.target.value)}
                placeholder="np. fb.com/ItCafeAndBar" disabled={form.fiscal_footer_facebook_enabled !== '1'} />
            </div>

            {/* Kod QR */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              <input type="checkbox" checked={form.fiscal_footer_qr_enabled === '1'}
                onChange={e => set('fiscal_footer_qr_enabled', e.target.checked ? '1' : '0')} />
              Kod QR
            </label>
            <div className="field" style={{ marginBottom: 16 }}>
              <input value={form.fiscal_footer_qr_url} onChange={e => set('fiscal_footer_qr_url', e.target.value)}
                placeholder="np. https://itcafeandbar.pl" disabled={form.fiscal_footer_qr_enabled !== '1'} />
            </div>

            {/* Dowolny tekst stopki */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
              <input type="checkbox" checked={form.fiscal_footer_text_enabled === '1'}
                onChange={e => set('fiscal_footer_text_enabled', e.target.checked ? '1' : '0')} />
              Dodatkowy tekst stopki
            </label>
            <div className="field">
              <input value={form.fiscal_footer_text} onChange={e => set('fiscal_footer_text', e.target.value)}
                placeholder="np. Zeskanuj kod QR lub wejdź na stronę..." disabled={form.fiscal_footer_text_enabled !== '1'} />
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* ZAKŁADKA: E-MAIL (SMTP + raport kasowy)                           */}
      {/* ================================================================ */}
      {tab === 'email' && (
        <div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Konfiguracja SMTP</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, marginBottom: 16 }}>
              Dane serwera pocztowego używane do wysyłki raportu kasowego przy zamknięciu.
            </p>

            <div className="field" style={{ marginBottom: 12 }}>
              <label>Host SMTP</label>
              <input value={form.smtp_host} onChange={e => set('smtp_host', e.target.value)} placeholder="np. smtp.gmail.com" />
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div className="field" style={{ margin: 0, flex: 1 }}>
                <label>Port</label>
                <input value={form.smtp_port} onChange={e => set('smtp_port', e.target.value)} placeholder="587" />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, paddingTop: 22 }}>
                <input type="checkbox" checked={form.smtp_secure === '1'}
                  onChange={e => set('smtp_secure', e.target.checked ? '1' : '0')} />
                SSL (port 465)
              </label>
            </div>

            <div className="field" style={{ marginBottom: 12 }}>
              <label>Login</label>
              <input value={form.smtp_user} onChange={e => set('smtp_user', e.target.value)} placeholder="np. kasa@itcafeandbar.pl" />
            </div>

            <div className="field" style={{ marginBottom: 12 }}>
              <label>Hasło</label>
              <input type="password" value={form.smtp_pass} onChange={e => set('smtp_pass', e.target.value)} placeholder="hasło / hasło aplikacji" />
            </div>

            <div className="field">
              <label>Adres nadawcy (opcjonalnie)</label>
              <input value={form.smtp_from} onChange={e => set('smtp_from', e.target.value)} placeholder="np. CafePOS <kasa@itcafeandbar.pl>" />
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>Raport kasowy — odbiorcy</h3>
            <div className="field">
              <label>Adresy e-mail (oddzielone przecinkiem)</label>
              <input value={form.cash_report_email_to} onChange={e => set('cash_report_email_to', e.target.value)}
                placeholder="np. wlasciciel@itcafeandbar.pl, ksiegowosc@itcafeandbar.pl" />
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* ZAKŁADKA: WIDOKI (widoczność menu per rola)                       */}
      {/* ================================================================ */}
      {tab === 'views' && (
        <>
          {roles.map(role => {
            const isAdmin = role.slug === 'admin';
            const visible = isAdmin ? MENU_ITEMS.map(m => m.key) : (role.visible_menu_keys || []);
            return (
              <div key={role.id} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 12, background: role.color_bg, color: role.color_text, padding: '3px 9px', borderRadius: 4, fontWeight: 500 }}>
                    {role.icon} {role.label}
                  </span>
                </div>
                {isAdmin && (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Admin zawsze widzi wszystko — nie można ograniczyć.</p>
                )}
                {!isAdmin && <div style={{ marginBottom: 8 }} />}
                {MENU_ITEMS.map(item => (
                  <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 14, cursor: isAdmin ? 'default' : 'pointer', opacity: isAdmin ? 0.6 : 1 }}>
                    <input type="checkbox" checked={visible.includes(item.key)} disabled={isAdmin}
                      onChange={() => toggleMenuVisible(role, item.key)} />
                    <span>{item.icon} {item.label}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </>
      )}

      {/* ================================================================ */}
      {/* ZAKŁADKA: KDS (wygląd ekranu kuchennego)                          */}
      {/* ================================================================ */}
      {tab === 'kds' && (
        <>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Czcionka</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Rozmiar tekstu na kartach zamówień (w pikselach).</p>
            <div style={{ display: 'flex', gap: 16 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>Nazwa pozycji</label>
                <input type="number" min="12" max="48" value={form.kds_font_name} onChange={e => set('kds_font_name', e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Wariant / modyfikatory / komentarz</label>
                <input type="number" min="10" max="40" value={form.kds_font_variant} onChange={e => set('kds_font_variant', e.target.value)} />
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>Kolory statusów</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Kolor liczby sztuk i akcentów dla pozycji gotowych / wydanych.</p>
            <div style={{ display: 'flex', gap: 16 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>Gotowe</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.kds_color_ready} onChange={e => set('kds_color_ready', e.target.value)} style={{ width: 44, height: 36, padding: 2, border: '0.5px solid var(--border)', borderRadius: 6 }} />
                  <input type="text" value={form.kds_color_ready} onChange={e => set('kds_color_ready', e.target.value)} style={{ flex: 1 }} />
                </div>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Wydane</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.kds_color_delivered} onChange={e => set('kds_color_delivered', e.target.value)} style={{ width: 44, height: 36, padding: 2, border: '0.5px solid var(--border)', borderRadius: 6 }} />
                  <input type="text" value={form.kds_color_delivered} onChange={e => set('kds_color_delivered', e.target.value)} style={{ flex: 1 }} />
                </div>
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>Wiek zamówienia</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Po ilu minutach licznik zmienia kolor (ostrzeżenie / alarm).</p>
            <div style={{ display: 'flex', gap: 16 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>Żółty (min)</label>
                <input type="number" min="1" max="120" value={Math.round(form.kds_age_warn / 60)}
                  onChange={e => set('kds_age_warn', String((parseInt(e.target.value) || 0) * 60))} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Czerwony (min)</label>
                <input type="number" min="1" max="180" value={Math.round(form.kds_age_danger / 60)}
                  onChange={e => set('kds_age_danger', String((parseInt(e.target.value) || 0) * 60))} />
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>Układ i dźwięk</h3>
            <div className="field">
              <label>Liczba kolumn siatki zamówień</label>
              <select value={form.kds_grid_columns} onChange={e => set('kds_grid_columns', e.target.value)}>
                <option value="auto">Automatycznie (dopasuj do ekranu)</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 4px', cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={form.kds_sound_enabled === '1'} onChange={e => set('kds_sound_enabled', e.target.checked ? '1' : '0')} />
              Dźwięk przy nowym zamówieniu
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={form.kds_show_category === '1'} onChange={e => set('kds_show_category', e.target.checked ? '1' : '0')} />
              Pokazuj kategorię pod nazwą pozycji
            </label>
          </div>
        </>
      )}

      {/* ================================================================ */}
      {/* ZAKŁADKA: FAKTURY                                                 */}
      {/* ================================================================ */}
      {tab === 'permissions' && (
        <div>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Raport kasowy</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Kto może otwierać i zamykać raport kasowy</p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {[
                { value: 'admin',   label: 'Admin' },
                { value: 'manager', label: 'Manager' },
                { value: 'waiter',  label: 'Kelner' },
              ].map(r => {
                const roles = (form.cash_report_close_roles || '').split(',').map(x => x.trim()).filter(Boolean);
                const checked = roles.includes(r.value);
                function toggle() {
                  const next = checked ? roles.filter(x => x !== r.value) : [...roles, r.value];
                  set('cash_report_close_roles', next.join(','));
                }
                return (
                  <label key={r.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={checked} onChange={toggle} />
                    {r.label}
                  </label>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Admin ma zawsze dostęp niezależnie od ustawień.</div>
          </div>
        </div>
      )}

      {tab === 'invoices' && (
        <>
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Numeracja faktur</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              Osobne liczniki dla faktur (FV) i paragonów z NIP (PN). Dostępne placeholdery
              w formacie: <code>{'{PREFIX}'}</code>, <code>{'{YEAR}'}</code>, <code>{'{MONTH}'}</code>, <code>{'{SEQ}'}</code> (kolejny
              numer, domyślnie 3 cyfry — użyj <code>{'{SEQ:4}'}</code> dla innej liczby cyfr).
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0 14px' }}>
              <div className="field"><label>Prefiks — Faktury (FV)</label>
                <input value={form.invoice_number_prefix_fv} onChange={e => set('invoice_number_prefix_fv', e.target.value)} placeholder="FV" />
              </div>
              <div className="field"><label>Format — Faktury (FV)</label>
                <input value={form.invoice_number_format_fv} onChange={e => set('invoice_number_format_fv', e.target.value)} placeholder="{PREFIX}/{YEAR}/{MONTH}/{SEQ}" />
              </div>
              <div className="field"><label>Reset licznika — Faktury (FV)</label>
                <select value={form.invoice_number_reset_fv} onChange={e => set('invoice_number_reset_fv', e.target.value)}>
                  <option value="yearly">Co roku</option>
                  <option value="monthly">Co miesiąc</option>
                </select>
              </div>
              <div />
              <div className="field"><label>Prefiks — Paragon z NIP (PN)</label>
                <input value={form.invoice_number_prefix_pn} onChange={e => set('invoice_number_prefix_pn', e.target.value)} placeholder="PN" />
              </div>
              <div className="field"><label>Format — Paragon z NIP (PN)</label>
                <input value={form.invoice_number_format_pn} onChange={e => set('invoice_number_format_pn', e.target.value)} placeholder="{PREFIX}/{YEAR}/{MONTH}/{SEQ}" />
              </div>
              <div className="field"><label>Reset licznika — Paragon z NIP (PN)</label>
                <select value={form.invoice_number_reset_pn} onChange={e => set('invoice_number_reset_pn', e.target.value)}>
                  <option value="yearly">Co roku</option>
                  <option value="monthly">Co miesiąc</option>
                </select>
              </div>
              <div />
            </div>
            <InvoiceSequenceEditor invoiceReset={form.invoice_number_reset_fv} receiptReset={form.invoice_number_reset_pn} />
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>KSeF — Krajowy System e-Faktur</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              Token produkcyjny do wysyłki faktur do Ministerstwa Finansów.
            </div>
            <div className="field"><label>Token API KSeF</label>
              <input type="password" value={form.ksef_token} onChange={e => set('ksef_token', e.target.value)} placeholder="Token produkcyjny z MF" />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Uzyskaj na <a href="https://www.podatki.gov.pl/ksef/" target="_blank" rel="noreferrer" style={{ color: 'var(--blue-dark)' }}>podatki.gov.pl/ksef</a>
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>Certyfikat KSeF — potwierdzenie transakcji (2 kody QR)</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              Potrzebny do wydruku "potwierdzenia transakcji" na drukarce fiskalnej, gdy faktura idzie
              do KSeF, ale nie ma jeszcze nadanego numeru. Wklej zawartość plików <code>.crt</code> i <code>.key</code> w
              całości (razem z liniami <code>-----BEGIN...-----</code>/<code>-----END...-----</code>). Bez tego wydrukuje się
              tylko pierwszy kod QR (weryfikacja faktury), bez drugiego (weryfikacja wystawcy).
            </div>
            <div className="field"><label>NIP kontekstu logowania w KSeF</label>
              <input value={form.ksef_context_nip} onChange={e => set('ksef_context_nip', e.target.value)} placeholder="Zwykle taki sam jak NIP sprzedawcy" />
            </div>
            <div className="field"><label>Certyfikat (.crt)</label>
              <textarea rows={6} value={form.ksef_cert_pem} onChange={e => set('ksef_cert_pem', e.target.value)}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 11, padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
            <div className="field"><label>Klucz prywatny (.key)</label>
              <textarea rows={6} value={form.ksef_cert_key_pem} onChange={e => set('ksef_cert_key_pem', e.target.value)}
                placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 11, padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box', resize: 'vertical' }} />
              <div style={{ fontSize: 11, color: 'var(--red, #A32D2D)', marginTop: 4 }}>
                ⚠ To wrażliwe dane — klucz prywatny pozwala podpisywać się jako Twoja firma w KSeF. Trzymaj dostęp do Ustawień ograniczony do zaufanych osób.
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13, marginTop: 4 }}>
              <input type="checkbox" checked={form.ksef_confirmation_auto_print === '1'} onChange={e => set('ksef_confirmation_auto_print', e.target.checked ? '1' : '0')} />
              Drukuj potwierdzenie transakcji automatycznie po utworzeniu faktury
            </label>
          </div>

          <div style={cardStyle}>
            <h3 style={sectionTitle}>GUS — wyszukiwanie kontrahentów po NIP</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              Klucz API REGON BIR do wyszukiwania danych firm po NIP. Pozostaw puste aby używać darmowego API Białej Listy MF (rekomendowane).
            </div>
            <div className="field"><label>Klucz API GUS (REGON BIR)</label>
              <input type="password" value={form.gus_api_key} onChange={e => set('gus_api_key', e.target.value)} placeholder="Pozostaw puste — używa API MF (darmowe)" />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Uzyskaj na <a href="https://api.stat.gov.pl/Home/RegonApi" target="_blank" rel="noreferrer" style={{ color: 'var(--blue-dark)' }}>api.stat.gov.pl</a>
              </div>
            </div>
          </div>
        </>
      )}

      </div>

      {/* Przycisk Zapisz */}
      <button className="btn-primary" style={{ width: 'auto', padding: '9px 24px', marginTop: 20 }} onClick={save}>
        {saved ? '✓ Zapisano' : 'Zapisz ustawienia'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Podgląd i ręczna korekta licznika numeracji (osobno dla FV i PN, per rok).
// Osobny komponent - działa na własnym stanie, niezależnie od reszty formularza
// ustawień (dane nie leżą w tabeli settings, tylko w invoice_sequences).
// ---------------------------------------------------------------------------
function InvoiceSequenceEditor({ invoiceReset = 'yearly', receiptReset = 'yearly' }) {
  const now = new Date();
  const year = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const monthFv = invoiceReset === 'monthly' ? currentMonth : 0;
  const monthPn = receiptReset === 'monthly' ? currentMonth : 0;
  const MONTH_NAMES = ['styczeń','luty','marzec','kwiecień','maj','czerwiec','lipiec','sierpień','wrzesień','październik','listopad','grudzień'];

  const [seqFv, setSeqFv] = React.useState(null);
  const [seqPn, setSeqPn] = React.useState(null);
  const [editing, setEditing] = React.useState(null); // 'invoice' | 'receipt_nip' | null
  const [newValue, setNewValue] = React.useState('');
  const [msg, setMsg] = React.useState('');

  function getToken() { return window.__cafeApiToken || localStorage.getItem('cafepos_token'); }

  async function load() {
    const token = getToken();
    const [rFv, rPn] = await Promise.all([
      fetch(`/api/invoices/meta/sequence?type=invoice&year=${year}&month=${monthFv}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`/api/invoices/meta/sequence?type=receipt_nip&year=${year}&month=${monthPn}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]);
    setSeqFv(rFv.seq); setSeqPn(rPn.seq);
  }

  React.useEffect(() => { load(); }, [invoiceReset, receiptReset]);

  async function save(type) {
    const token = getToken();
    const month = type === 'invoice' ? monthFv : monthPn;
    try {
      const res = await fetch('/api/invoices/meta/sequence', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type, year, month, seq: parseInt(newValue) || 0 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMsg('✓ Licznik zaktualizowany');
      setEditing(null);
      load();
    } catch (err) { setMsg(`✗ ${err.message}`); }
  }

  const rowStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '0.5px solid var(--border)' };
  const periodLabel = (mode, month) => mode === 'monthly' ? `${MONTH_NAMES[month - 1]} ${year}` : `${year}`;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
        Bieżący licznik
      </div>
      {msg && <div style={{ fontSize: 12, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red, #A32D2D)', marginBottom: 4 }}>{msg}</div>}

      <div style={rowStyle}>
        <span style={{ fontSize: 13, minWidth: 140 }}>Faktury (FV)<br /><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{periodLabel(invoiceReset, monthFv)}</span></span>
        {editing === 'invoice' ? (
          <>
            <input type="number" value={newValue} onChange={e => setNewValue(e.target.value)} style={{ width: 90 }}
              placeholder={String(seqFv)} />
            <button className="btn-sm" onClick={() => save('invoice')}>Zapisz</button>
            <button className="btn-sm" onClick={() => setEditing(null)}>Anuluj</button>
          </>
        ) : (
          <>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{seqFv ?? '…'}</span>
            <button className="btn-sm" onClick={() => { setEditing('invoice'); setNewValue(String(seqFv || 0)); }}>Zmień</button>
          </>
        )}
      </div>

      <div style={rowStyle}>
        <span style={{ fontSize: 13, minWidth: 140 }}>Paragon z NIP (PN)<br /><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{periodLabel(receiptReset, monthPn)}</span></span>
        {editing === 'receipt_nip' ? (
          <>
            <input type="number" value={newValue} onChange={e => setNewValue(e.target.value)} style={{ width: 90 }}
              placeholder={String(seqPn)} />
            <button className="btn-sm" onClick={() => save('receipt_nip')}>Zapisz</button>
            <button className="btn-sm" onClick={() => setEditing(null)}>Anuluj</button>
          </>
        ) : (
          <>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{seqPn ?? '…'}</span>
            <button className="btn-sm" onClick={() => { setEditing('receipt_nip'); setNewValue(String(seqPn || 0)); }}>Zmień</button>
          </>
        )}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
        Ustawiona wartość to numer <strong>ostatniej</strong> wystawionej faktury — kolejna dostanie numer o 1 wyższy.
        Zmiana wymaga roli Admin. Okres (rok/miesiąc) zależy od wybranego wyżej trybu resetu — zmień go
        i zapisz ustawienia, żeby zobaczyć/edytować licznik dla innego okresu.
      </div>
    </div>
  );
}
